import React, { FC, ReactElement } from 'react';
import { Helmet } from 'react-helmet';
import { graphql, useStaticQuery } from 'gatsby';

interface Props {
  description?: string;
  lang?: string;
  meta?: any[];
  title: string;
}

const QUERY = graphql`
  query {
    site {
      siteMetadata {
        title
        description
        author {
          name
        }
      }
    }
  }
`;

const schema = {
  '@context': `http://schema.org`,
  '@type': `SoftwareApplication`,
  '@id': `https://pentester.com/#softwareapplication`,
  name: `Pentester`,
  image: `https://pentester.com/img/risks_light.svg`,
  url: `https://pentester.com/`,
  description: `Pentester is a powerful software application designed for comprehensive security testing and penetration testing.`,
  applicationCategory: `Security`,
  operatingSystem: [`Windows`, `Mac`, `Linux`],
  screenshot: [
    `https://pentester.com/img/screenshot1.webp`,
    `https://pentester.com/img/screenshot2.webp`,
    `https://pentester.com/img/screenshot3.webp`,
  ],
  offers: {
    '@type': `Offer`,
    price: `49.99`,
    priceCurrency: `USD`,
  },
  author: {
    '@type': `Organization`,
    name: `Pentester LLC`,
    url: `https://pentester.com/`,
  },
};

export const SEO: FC<Props> = ({
  description,
  lang,
  meta,
  title,
}): ReactElement => {
  const { site } = useStaticQuery(QUERY);
  const metaDescription = description || site.siteMetadata.description;
  const defaultTitle = site.siteMetadata?.title;

  return (
    <Helmet
      htmlAttributes={{ lang }}
      title={title}
      titleTemplate={defaultTitle ? `%s | ${defaultTitle}` : null}
      meta={[
        {
          name: `description`,
          content: metaDescription,
        },
        {
          property: `og:title`,
          content: `Pentester`,
        },
        {
          property: `og:description`,
          content: `Pentester is a powerful software application designed for comprehensive security testing and penetration testing.`,
        },
        {
          property: `og:url`,
          content: `https://pentester.com/`,
        },
        {
          property: `og:image`,
          content: `https://pentester.com/img/risks_light.svg`,
        },
        {
          property: `og:type`,
          content: `website`,
        },
        {
          name: `twitter:card`,
          content: `summary_large_image`,
        },
        {
          name: `twitter:image`,
          content: `https://pentester.com/img/risks_light.svg`,
        },
        {
          property: `twitter:url`,
          content: `https://pentester.com/`,
        },
        {
          name: `twitter:title`,
          content: `Pentester`,
        },
        {
          name: `twitter:description`,
          content: `Pentester is a powerful software application designed for comprehensive security testing and penetration testing.`,
        },
        {
          name: `viewport`,
          content: `width=device-width, initial-scale=1, maximum-scale=5, user-scalable=yes, minimal-ui`,
        },
      ].concat(meta)}
    >
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

SEO.defaultProps = {
  lang: `en`,
  meta: [],
  description: ``,
};
